import React, { useMemo, useState } from 'react';
import {
  Image,
  Pressable,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { HouseholdViewSwitcher } from '../household/HouseholdViewSwitcher';
import { InviteHouseholdModal } from '../household/InviteHouseholdModal';
import { NotificationBell } from '../notifications/NotificationBell';
import { BrandMark } from './BrandMark';
import { APP_NAME } from '../../config/branding';
import { useAuth } from '../../contexts/AuthContext';
import { useAppLayout } from '../../hooks/useAppLayout';
import {
  navigateToAuth,
  navigateToHome,
  navigateToLanding,
  navigateToSettings,
} from '../../core/navigation/navigationRef';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { telemetry } from '../../services/telemetry';

/**
 * Web top toolbar in normal document flow — Sign in / account menu.
 */
export function AppTopBar() {
  const insets = useSafeAreaInsets();
  const { sidebar } = useAppLayout();
  const compact = !sidebar;
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const isPro = user?.plan === 'pro';

  const initials = useMemo(() => {
    const source = user?.name?.trim() || user?.email?.trim() || '?';
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
    }
    return source.slice(0, 2).toUpperCase();
  }, [user?.email, user?.name]);

  const handleSignOut = async () => {
    setMenuOpen(false);
    await logout();
    navigateToLanding();
  };

  return (
    <View style={[styles.bar, { paddingTop: Math.max(insets.top, spacing.sm) }]}>
      <View style={styles.inner}>
        {compact ? (
          <Pressable
            accessibilityRole="link"
            accessibilityLabel={`${APP_NAME} home`}
            onPress={navigateToHome}
            style={({ pressed }) => [styles.brandRow, pressed && styles.pressed]}
          >
            <BrandMark size={32} />
          </Pressable>
        ) : null}
        <View style={styles.trailingGrow} />

        {isLoading ? (
          <View style={styles.trailingSlot} />
        ) : isAuthenticated ? (
          <View style={styles.trailingRow}>
            <HouseholdViewSwitcher compact />
            <NotificationBell />
            {!compact ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Invite to household"
                onPress={() => setInviteOpen(true)}
                style={({ pressed }) => [styles.inviteBtn, pressed && styles.pressed]}
              >
                <Text style={styles.inviteText}>Invite</Text>
              </Pressable>
            ) : null}
          <View style={styles.menuWrap}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={isPro ? 'Account menu, Pro' : 'Account menu'}
              onPress={() => setMenuOpen((o) => !o)}
              style={({ pressed }) => [
                compact ? styles.avatarOnly : styles.avatarChip,
                isPro && styles.avatarPro,
                pressed && styles.pressed,
              ]}
            >
              {user?.picture ? (
                <Image
                  source={{ uri: user.picture }}
                  style={compact ? styles.avatarFill : styles.avatar}
                />
              ) : (
                <View style={compact ? styles.fallbackFill : styles.fallback}>
                  <Text style={styles.initials}>{initials}</Text>
                </View>
              )}
              {!compact ? (
                <Text style={styles.name} numberOfLines={1}>
                  {user?.name ?? 'Account'}
                </Text>
              ) : null}
              {isPro ? <Text style={styles.proBadge}>Pro</Text> : null}
            </Pressable>

            {menuOpen ? (
              <View style={styles.menu}>
                {user?.email ? (
                  <Text style={styles.menuEmail} numberOfLines={1}>
                    {user.email}
                  </Text>
                ) : null}
                {isPro ? <Text style={styles.menuPro}>Pro member</Text> : null}
                {compact ? (
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => {
                      setMenuOpen(false);
                      setInviteOpen(true);
                    }}
                    style={({ pressed }) => [styles.menuItem, pressed && styles.pressed]}
                  >
                    <Text style={styles.menuItemText}>Invite household</Text>
                  </Pressable>
                ) : null}
                {!isPro ? (
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => {
                      setMenuOpen(false);
                      void telemetry.logUpgradeEntry('account_menu');
                      navigateToSettings();
                    }}
                    style={({ pressed }) => [styles.menuItem, pressed && styles.pressed]}
                  >
                    <Text style={styles.menuItemText}>Subscribe</Text>
                  </Pressable>
                ) : null}
                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    setMenuOpen(false);
                    navigateToSettings();
                  }}
                  style={({ pressed }) => [styles.menuItem, pressed && styles.pressed]}
                >
                  <Text style={styles.menuItemText}>Settings</Text>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => void handleSignOut()}
                  style={({ pressed }) => [styles.menuItem, pressed && styles.pressed]}
                >
                  <Text style={styles.menuItemDanger}>Sign out</Text>
                </Pressable>
              </View>
            ) : null}
          </View>
          <InviteHouseholdModal visible={inviteOpen} onClose={() => setInviteOpen(false)} />
          </View>
        ) : (
          <View style={styles.trailingRow}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Subscribe"
              onPress={() => {
                void telemetry.logUpgradeEntry('topbar');
                navigateToSettings();
              }}
              style={({ pressed }) => [styles.signInBtn, pressed && styles.pressed]}
            >
              <Text style={styles.signInText}>Subscribe</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Sign in"
              onPress={navigateToAuth}
              style={({ pressed }) => [styles.signInBtn, pressed && styles.pressed]}
            >
              <Text style={styles.signInText}>Sign in</Text>
            </Pressable>
          </View>
        )}
      </View>
    </View>
  );
}

const AVATAR = 32;

const styles = StyleSheet.create({
  bar: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
    backgroundColor: 'rgba(11, 15, 20, 0.72)',
    backdropFilter: 'blur(20px) saturate(160%)',
    WebkitBackdropFilter: 'blur(20px) saturate(160%)',
    zIndex: 20,
  } as ViewStyle,
  inner: {
    minHeight: 48,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing.md,
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginRight: 'auto',
    cursor: 'pointer',
  } as ViewStyle,
  trailingGrow: {
    flex: 1,
  },
  trailingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    gap: spacing.sm,
  },
  inviteBtn: {
    minHeight: 34,
    paddingHorizontal: spacing.md,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.4)',
    backgroundColor: 'rgba(61, 214, 164, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  } as ViewStyle,
  inviteText: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
  },
  trailingSlot: {
    width: AVATAR,
    height: AVATAR,
  },
  signInBtn: {
    minHeight: 36,
    paddingHorizontal: spacing.md,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.4)',
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  } as ViewStyle,
  signInText: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
  },
  menuWrap: {
    position: 'relative',
    zIndex: 30,
  },
  avatarOnly: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: AVATAR,
    cursor: 'pointer',
  } as ViewStyle,
  avatarChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    maxWidth: 240,
    paddingVertical: 3,
    paddingLeft: 3,
    paddingRight: spacing.md,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.14)',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    cursor: 'pointer',
  } as ViewStyle,
  avatarPro: {
    borderColor: 'rgba(61, 214, 164, 0.55)',
  },
  avatar: {
    width: AVATAR - 4,
    height: AVATAR - 4,
    borderRadius: (AVATAR - 4) / 2,
  },
  avatarFill: {
    width: AVATAR,
    height: AVATAR,
    borderRadius: AVATAR / 2,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.14)',
  },
  fallback: {
    width: AVATAR - 4,
    height: AVATAR - 4,
    borderRadius: (AVATAR - 4) / 2,
    backgroundColor: 'rgba(61, 214, 164, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fallbackFill: {
    width: AVATAR,
    height: AVATAR,
    borderRadius: AVATAR / 2,
    backgroundColor: 'rgba(61, 214, 164, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.14)',
  },
  initials: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
  },
  name: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.text,
    flexShrink: 1,
  },
  proBadge: {
    ...typography.caption,
    fontSize: 10,
    fontWeight: '800',
    color: colors.accent,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.5)',
    backgroundColor: 'rgba(61, 214, 164, 0.14)',
    overflow: 'hidden',
  },
  menu: {
    position: 'absolute',
    top: AVATAR + spacing.sm,
    right: 0,
    minWidth: 196,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
    backgroundColor: 'rgba(12, 18, 28, 0.95)',
    backdropFilter: 'blur(20px)',
    overflow: 'hidden',
    boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45)',
  } as ViewStyle,
  menuEmail: {
    ...typography.caption,
    color: colors.textMuted,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xs,
  },
  menuPro: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xs,
  },
  menuItem: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    cursor: 'pointer',
  } as ViewStyle,
  menuItemText: {
    ...typography.body,
    fontSize: 15,
    color: colors.text,
  },
  menuItemDanger: {
    ...typography.body,
    fontSize: 15,
    color: colors.danger,
  },
  pressed: {
    opacity: 0.85,
  },
});
